import { GoalTOCModel } from '@models/goalTocs';
import { GoalVisibilityStatus } from '@models/goals';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
import { useGoalTOCService } from './useGoalTOCService';

export interface AssignmentMapEntry {
  goalSubscriptionId: number;
  nodeEntityId: string;
  nextNodeEntityId: string | undefined;
  prevNodeEntityId: string | undefined;
}

interface UseTocParams {
  goalEntityId: string;
  goalSubscriptionId: number;
  enabled: boolean; // Only load once this is true
}

interface UseTocResult {
  toc: GoalTOCModel | null;
  assignments: AssignmentMapEntry[];
  assignmentsMap: Map<string, AssignmentMapEntry> | undefined;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Loads a goal's TOC once and derives the assignments navigation map (next/prev per node,
 * skipping WIP child goals).
 *
 * @param params - goal/subscription to load, and an `enabled` gate
 * @returns TOC data, the derived assignments map, loading state, and error
 */
export const useToc = ({
  goalEntityId,
  goalSubscriptionId,
  enabled
}: UseTocParams): UseTocResult => {
  const { getToc } = useGoalTOCService();

  const [toc, setToc] = useState<GoalTOCModel | null>(null);
  const [assignments, setAssignments] = useState<AssignmentMapEntry[]>([]);
  const [assignmentsMap, setAssignmentsMap] = useState<Map<string, AssignmentMapEntry> | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef<boolean>(true);

  /**
   * Process TOC response and extract assignments data
   */
  const processTocData = (response: GoalTOCModel) => {
    setToc(response);

    let taskList: AssignmentMapEntry[] = [];
    for (const goalSection of response.goalSections) {
      if (goalSection.goal.visibilityStatus === GoalVisibilityStatus.WIP) continue;
      for (const strategySection of goalSection.strategySections) {
        for (const task of strategySection.tasks) {
          taskList.push({
            goalSubscriptionId: goalSection.goal.subscription?.id,
            nodeEntityId: task.nodeEntityId,
            nextNodeEntityId: null,
            prevNodeEntityId: null
          });
        }
      }
    }

    // TODO : optimize later
    const newAssignmentsMap = new Map<string, AssignmentMapEntry>();
    taskList = taskList.map((task, idx) => {
      const nextTask = taskList[idx + 1];
      const prevTask = taskList[idx - 1];
      const entry = {
        ...task,
        nextNodeEntityId: nextTask?.nodeEntityId,
        prevNodeEntityId: prevTask?.nodeEntityId
      };
      // TODO: Consider unique key like combination of goalSubscriptionId and nodeEntityId
      newAssignmentsMap.set(task.nodeEntityId, entry);
      return entry;
    });

    setAssignmentsMap(newAssignmentsMap);
    setAssignments(taskList as AssignmentMapEntry[]);
  };

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      return;
    }

    setIsLoading(true);
    setError(null);

    getToc({ goalEntityId, goalSubscriptionId })
      .then((response) => {
        if (!isMountedRef.current) return;
        processTocData(response);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        const errorObject = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(errorObject);
        setError(errorObject);
        setToc(null);
        setAssignments([]);
        setAssignmentsMap(undefined);
      })
      .finally(() => {
        if (isMountedRef.current) setIsLoading(false);
      });

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalEntityId, goalSubscriptionId, enabled]);

  return {
    toc,
    assignments,
    assignmentsMap,
    isLoading,
    error
  };
};
